import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { RichTextEditor } from "@/components/admin/RichTextEditor";

interface Event {
  id: number;
  title: string;
  event_date?: string;
}

interface VenueContent {
  id?: number;
  location: string;
  event_date: string;
  venue_description: string | null;
  map_embed_url: string | null;
  hotel_name: string | null;
  phone: string | null;
  address: string | null;
  directions_link: string | null;
  directions: string | null;
  venue_image_url: string | null;
}

const emptyVenue: VenueContent = {
  location: "",
  event_date: "",
  venue_description: null,
  map_embed_url: null,
  hotel_name: null,
  phone: null,
  address: null,
  directions_link: null,
  directions: null,
  venue_image_url: null,
};

export function VenueEditor() {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [content, setContent] = useState<VenueContent>(emptyVenue);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadContent(selectedEventId);
    }
  }, [selectedEventId]);

  const loadEvents = async () => {
    try {
      const response = await api.get<Event[]>("/api/events");
      if (response.success && response.data) {
        setEvents(response.data);
        if (response.data.length > 0) {
          setSelectedEventId(response.data[0].id);
        } else {
          setInitialLoading(false);
        }
      } else {
        setInitialLoading(false);
      }
    } catch {
      setInitialLoading(false);
      toast.error("Failed to load events");
    }
  };

  const loadContent = async (eventId: number) => {
    try {
      setInitialLoading(true);
      const response = await api.get<VenueContent>(`/api/venues/${eventId}`);
      if (response.success && response.data) {
        setContent({
          id: response.data.id,
          location: response.data.location || "",
          event_date: response.data.event_date || "",
          venue_description: response.data.venue_description || null,
          map_embed_url: response.data.map_embed_url || null,
          hotel_name: response.data.hotel_name || null,
          phone: response.data.phone || null,
          address: response.data.address || null,
          directions_link: response.data.directions_link || null,
          directions: response.data.directions || null,
          venue_image_url: response.data.venue_image_url || null,
        });
      }
    } catch {
      // No venue details yet for this event — show empty form
      setContent({ ...emptyVenue, id: eventId });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedEventId) {
      toast.error("Please select an event first");
      return;
    }
    // Validation
    if (!content.location.trim()) {
      toast.error("Location is required");
      return;
    }
    if (!content.event_date.trim()) {
      toast.error("Event date is required");
      return;
    }

    setLoading(true);
    try {
      // Venue is stored directly on the events row — always PUT /:id
      const response = await api.put<VenueContent>(`/api/venues/${selectedEventId}`, content);
      if (response.success) {
        toast.success("Venue content updated successfully");
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update venue content";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof VenueContent, value: string) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await api.upload(file);
      if (response.success && response.data?.imageUrl) {
        const imageUrl = response.data.imageUrl;
        setContent((prev) => ({
          ...prev,
          venue_image_url: imageUrl,
        }));
        toast.success("Image uploaded successfully");
      } else {
        toast.error(response.error || "Failed to upload image");
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to upload image";
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading venue details...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-primary mb-2">Venue Details</h1>
        <p className="text-muted-foreground">
          Manage venue details per event.
        </p>
      </div>

      {/* Event selector */}
      <Card className="p-6">
        <div>
          <Label htmlFor="venue-event">Select Event</Label>
          <Select
            value={selectedEventId?.toString() || ""}
            onValueChange={(value) => setSelectedEventId(parseInt(value))}
          >
            <SelectTrigger id="venue-event" className="mt-1">
              <SelectValue placeholder="Select an event" />
            </SelectTrigger>
            <SelectContent>
              {events.map((event) => (
                <SelectItem key={event.id} value={event.id.toString()}>
                  {event.title}
                  {event.event_date && ` (${event.event_date})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {selectedEventId && (
        <Card className="p-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="venue_image">Venue Image</Label>
            <div className="mt-2">
              {content.venue_image_url ? (
                <div className="relative w-full h-48 rounded-lg overflow-hidden border border-border">
                  <img
                    src={content.venue_image_url}
                    alt="Venue"
                    className="w-full h-full object-cover"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={() => handleChange("venue_image_url", "")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center hover:border-accent transition-colors">
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-2">Click to upload venue image</p>
                  <Input
                    id="venue_image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="max-w-xs"
                    disabled={uploading}
                  />
                  {uploading && (
                    <p className="text-sm text-muted-foreground mt-1">Uploading...</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={content.location}
              onChange={(e) => handleChange("location", e.target.value)}
              placeholder="e.g., Doha, Qatar"
            />
          </div>

          <div>
            <Label htmlFor="event_date">Event Date</Label>
            <Input
              id="event_date"
              value={content.event_date}
              onChange={(e) => handleChange("event_date", e.target.value)}
              placeholder="e.g., Thursday, 25 April 2024"
            />
          </div>

          <div>
            <Label htmlFor="venue_description">Venue Description</Label>
            <RichTextEditor
              content={content.venue_description || ""}
              onChange={(val) => handleChange("venue_description", val)}
              placeholder="Enter description about the venue, facilities, and hospitality"
            />
          </div>

          <div>
            <Label htmlFor="map_embed_url">Map Embed URL</Label>
            <Input
              id="map_embed_url"
              value={content.map_embed_url || ""}
              onChange={(e) => handleChange("map_embed_url", e.target.value)}
              placeholder="Enter the embed URL for the map (e.g., OpenStreetMap embed URL)"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Example: https://www.openstreetmap.org/export/embed.html?bbox=51.45%2C25.25%2C51.6%2C25.35&layer=mapnik
            </p>
          </div>

          <div>
            <Label htmlFor="hotel_name">Hotel Name</Label>
            <Input
              id="hotel_name"
              value={content.hotel_name || ""}
              onChange={(e) => handleChange("hotel_name", e.target.value)}
              placeholder="e.g., Sheraton Grand Doha Resort & Convention Hotel"
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={content.phone || ""}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="e.g., +974 448-54444"
            />
          </div>

          <div>
            <Label htmlFor="address">Full Address</Label>
            <RichTextEditor
              content={content.address || ""}
              onChange={(val) => handleChange("address", val)}
              placeholder="Enter the full address"
              minHeight="80px"
            />
          </div>

          <div>
            <Label htmlFor="directions_link">Maps & Directions Link</Label>
            <Input
              id="directions_link"
              value={content.directions_link || ""}
              onChange={(e) => handleChange("directions_link", e.target.value)}
              placeholder="Enter the URL for directions (e.g., Google Maps link)"
            />
          </div>

          <div>
            <Label htmlFor="directions">Directions</Label>
            <RichTextEditor
              content={content.directions || ""}
              onChange={(val) => handleChange("directions", val)}
              placeholder="Enter detailed directions to the venue"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={loading || uploading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </Card>
      )}
    </div>
  );
}
